import { getExam, getLanguage } from './config/examStructure';

export interface LanguageRuntimeContext {
  languageId: string;
  languageName: string;
  languageNameEn: string;
  locale: string;
  ttsLocale: string;
  examTypes: string[];
  examNames: string[];
}

export interface ExamLanguageContext extends Omit<
  LanguageRuntimeContext,
  'examTypes' | 'examNames'
> {
  examType: string;
  examName: string;
  examFullName: string;
}

const LANGUAGE_RUNTIME_DEFAULTS: Record<string, { locale: string; ttsLocale: string }> = {
  english: { locale: 'en-US', ttsLocale: 'en-US' },
  french: { locale: 'fr-FR', ttsLocale: 'fr-FR' },
  spanish: { locale: 'es-ES', ttsLocale: 'es-ES' },
  japanese: { locale: 'ja-JP', ttsLocale: 'ja-JP' },
  korean: { locale: 'ko-KR', ttsLocale: 'ko-KR' },
};

const EXAM_TTS_LOCALE_OVERRIDES: Record<string, string> = {
  ielts: 'en-GB',
  toefl: 'en-US',
};

export function normalizeExamType(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

export function getLanguageRuntimeContext(languageId: string): LanguageRuntimeContext | null {
  const language = getLanguage(languageId);
  if (!language) return null;

  const defaults = LANGUAGE_RUNTIME_DEFAULTS[language.id] ?? { locale: 'und', ttsLocale: 'und' };

  const liveExams = language.exams.filter((exam) => exam.available);

  return {
    languageId: language.id,
    languageName: language.name,
    languageNameEn: language.nameEn,
    locale: defaults.locale,
    ttsLocale: defaults.ttsLocale,
    examTypes: liveExams.map((exam) => exam.id),
    examNames: liveExams.map((exam) => exam.name),
  };
}

export function getExamTypesForLanguage(languageId: string): string[] {
  return getLanguageRuntimeContext(languageId)?.examTypes ?? [];
}

/** All exam ids under a language, including unavailable catalog entries. */
export function getCatalogExamTypesForLanguage(languageId: string): string[] {
  return (
    getLanguage(languageId)
      ?.exams.map((exam) => exam.id)
      .sort() ?? []
  );
}

export function getLanguageIdsForExamTypes(examTypes: string[]): string[] {
  const languageIds = new Set<string>();
  for (const examType of examTypes) {
    const context = getExamLanguageContext(examType);
    if (context) {
      languageIds.add(context.languageId);
    }
  }
  return Array.from(languageIds).sort();
}

export function isExamInLanguage(examType: string, languageId: string): boolean {
  const normalizedExamType = normalizeExamType(examType);
  return getCatalogExamTypesForLanguage(languageId).includes(normalizedExamType);
}

export function getExamLanguageContext(examType: string): ExamLanguageContext | null {
  const normalizedExamType = normalizeExamType(examType);
  const result = getExam(normalizedExamType);
  if (!result) return null;

  const languageContext = getLanguageRuntimeContext(result.language.id);
  if (!languageContext) return null;

  return {
    examType: result.exam.id,
    examName: result.exam.name,
    examFullName: result.exam.fullName,
    languageId: languageContext.languageId,
    languageName: languageContext.languageName,
    languageNameEn: languageContext.languageNameEn,
    locale: languageContext.locale,
    ttsLocale: EXAM_TTS_LOCALE_OVERRIDES[result.exam.id] ?? languageContext.ttsLocale,
  };
}
